import React,{useCallback,useEffect,useLayoutEffect,useMemo,useRef,useState} from 'react';
import {FiAlertTriangle,FiArrowLeft,FiArrowRight,FiDownload,FiInfo,FiMinus,FiPause,FiPlay,FiPlus,FiRepeat,FiRotateCcw,FiScissors,FiSkipBack,FiSkipForward,FiTrash2} from 'react-icons/fi';
import RefineWaveform from './RefineWaveform.jsx';
import {movePassage,protectLegacyWordEdges} from '../lib/paperEdit.js';
import {checkpointBeforeAiChange} from '../lib/storyVersions.js';
import {activeCutName} from '../lib/assemblyCuts.js';
import {assistWaveformPassages} from '../lib/waveformAssist.js';
import {waveformAssistEnabled} from '../lib/boundaryFeature.js';
import {refinePlaybackEndAction,shouldResumeRefineSequence} from '../lib/refinePlaybackLoop.js';
import {passageInsertSlot,passageReorderTarget} from '../lib/storyPointerReorder.js';
import {createFloatingPassageDrag} from '../lib/floatingPassageDrag.js';
import {capturePassageRects,animatePassageReorder} from '../lib/passageMotion.js';
import {removeStoryPassage,renameStorySection,moveStorySection,removeStorySection,placeStorySection,placeStorySectionBeside} from '../lib/storySections.js';
import {projectInterviews} from '../lib/projectWorkspace.js';
import {storySourceDisplayName} from '../lib/interviewNames.js';
import StorySectionHeading from './StorySectionHeading.jsx';
import {interviewColour,sourceColourOrder} from '../lib/storyContext.js';
import {sourceFrameStep,sourceBounds,snapSourceTime,refineTrim,prepareRefine,refineSignals,formatSourceFrames,restoreLockHandles} from '../lib/refineEdit.js';
import './RefineReview.css';

const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
const clock=value=>{
 const n=Math.max(0,Number(value)||0);
 const h=Math.floor(n/3600),m=Math.floor(n%3600/60),s=Math.floor(n%60);
 return (h?String(h).padStart(2,'0')+':':'')+String(m).padStart(2,'0')+':'+String(s).padStart(2,'0');
};
const title=(source,block)=>source?.displayName||source?.title||source?.filename||block?.sourceId||'Interview';
const typing=el=>Boolean(el?.closest?.('input,textarea,select,[contenteditable="true"],[role="textbox"]'));
const pixels=[10,18,30,48];

export default function RefineReview({project,rows=[],activeId,getUrl,localUrl='',onChange,onBack,onExport,busy=false,playbackRef}){
 const paper=project.paperEdit||{sources:{},blocks:[]},blocks=paper.blocks||[];
 const [index,setIndex]=useState(0),active=clamp(index,0,Math.max(0,blocks.length-1));
 const block=blocks[active],next=blocks[active+1],source=paper.sources?.[block?.sourceId];
 const [mainUrl,setMainUrl]=useState(''),[nextUrl,setNextUrl]=useState('');
 // A loaded passage ID, not only its URL, gates automatic playback. Adjacent
 // clips often share the same media URL and do not emit another canplay event.
 const [loadedBlockId,setLoadedBlockId]=useState(null);
 const [loadedNextId,setLoadedNextId]=useState(null);
 const [waveformUrls,setWaveformUrls]=useState({});
 const [draggingCard,setDraggingCard]=useState(null),[dropSlot,setDropSlot]=useState(null),[headingDrop,setHeadingDrop]=useState(null);
 const [loading,setLoading]=useState(false),[mediaError,setMediaError]=useState('');
 const [analysingEdges,setAnalysingEdges]=useState(false);
 const [playing,setPlaying]=useState(false),[showNext,setShowNext]=useState(false),[nextReady,setNextReady]=useState(false);
 const [loopEnabled,setLoopEnabled]=useState(false);
 // A ref makes the toggle apply immediately to media timing callbacks.
 const loopRef=useRef(false);
 const toggleLoop=()=>{
  loopRef.current=!loopRef.current;
  setLoopEnabled(loopRef.current);
 };
 const [playhead,setPlayhead]=useState(0),[zoom,setZoom]=useState(1),[detailZoom,setDetailZoom]=useState(3);
 const [selectedEdge,setSelectedEdge]=useState(null),[draft,setDraft]=useState(null),[message,setMessage]=useState('');
 const mainVideo=useRef(null),nextVideo=useRef(null),playRef=useRef(null),request=useRef(0);
 const sourceCache=useRef(new Map()),rowsRef=useRef(rows),urlRef=useRef(getUrl);
 const paperRef=useRef(paper),projectRef=useRef(project),dragRef=useRef(null),detailRef=useRef(null);
 const scrollRef=useRef(null),autoRef=useRef(false),sequenceRef=useRef(false),seekRef=useRef(null);
 const assemblyRef=useRef(null),cardDragRef=useRef(null),cardScrollRaf=useRef(null),headingDrag=useRef(null);
 const reorderPositions=useRef(null),reorderAnimations=useRef([]),suppressClickUntil=useRef(0);
 rowsRef.current=rows;urlRef.current=getUrl;paperRef.current=paper;projectRef.current=project;
 const interviews=projectInterviews(rows,activeId);
 const colourSources=sourceColourOrder(paper.sourceColourOrder,[
  ...interviews.map(interview=>interview.id),...Object.keys(paper.sources||{})
 ]);
 const sourceName=id=>storySourceDisplayName(interviews.find(item=>item.id===id),paper.sources?.[id]);

 const total=blocks.reduce((sum,part)=>sum+Math.max(0,part.end-part.start),0);
 const px=pixels[zoom],step=sourceFrameStep(source)||.01;
 const changes=paper.refine?.lockedHandles?.[block?.id]||block?.boundaryBaseline||(block?.automaticHandles
  ? {start:block.start,end:block.end}:block?.originalRange);
 const lockedEdges=paper.refine?.lockedHandles?.[block?.id];
 const manuallyTrimmed=Boolean(block&&lockedEdges&&
  (Math.abs(lockedEdges.start-block.start)>.00001||Math.abs(lockedEdges.end-block.end)>.00001));
 const canAutoEdges=Boolean(waveformAssistEnabled&&block?.originalRange&&block?.automaticHandles&&!manuallyTrimmed);
 const changed=Boolean(changes&&block&&(Math.abs(changes.start-block.start)>.00001||Math.abs(changes.end-block.end)>.00001));
 const warnings=block?refineSignals(block,source).filter(w=>w.severity==='warning'):[];
 const hasLegacyWordEdges=useMemo(()=>protectLegacyWordEdges(paper)!==paper,[paper]);
 const inPoint=draft&&draft.id===block?.id&&draft.edge==='start'?draft.value:block?.start||0;
 const outPoint=draft&&draft.id===block?.id&&draft.edge==='end'?draft.value:block?.end||0;
 const sourceMax=block?sourceBounds(block,source).max:0;
 const pad=detailZoom,windowStart=block?Math.max(0,block.start-pad):0;
 // Keep the source scale fixed during a drag; moving the viewport under the pointer makes handles jump.
 const windowEnd=block?Math.min(sourceMax,Math.max(block.end+pad,windowStart+step*2)):1;
 const windowLength=Math.max(step,windowEnd-windowStart);
 const percent=value=>100*clamp((value-windowStart)/windowLength,0,1);
 const selectText=(part)=>{
  const src=paper.sources?.[part.sourceId],sourceWords=src?.words;
  const original=paper.refine?.lockedHandles?.[part.id]||part.originalRange;
  if(!Array.isArray(sourceWords)||!original||
   (Math.abs(original.start-part.start)<.00001&&Math.abs(original.end-part.end)<.00001))
   return part.text||'Untitled select';
  const trimmed=sourceWords.filter(w=>w.end>part.start&&w.start<part.end).map(w=>w.text?.trim()).filter(Boolean).join(' ');
  return trimmed||part.text||'Untitled select';
 };
 const timeline=useMemo(()=>{
  let offset=0;
  return blocks.map(part=>{
   const length=Math.max(0,part.end-part.start),src=paper.sources?.[part.sourceId];
   const item={...part,offset,duration:length,source:src};offset+=length;return item;
  });
 },[blocks,paper.sources]);
 const stop=useCallback(()=>{
  playRef.current=null;sequenceRef.current=false;autoRef.current=false;
  mainVideo.current?.pause();nextVideo.current?.pause();
  setPlaying(false);setShowNext(false);
 },[]);
 const resolveUrl=useCallback(async id=>{
  const row=rowsRef.current.find(item=>item.id===id);
  if(!row||!row.media_ready){
   if(id===activeId&&localUrl)return localUrl;
   throw new Error('Source video unavailable. Reconnect it in Paper Edit.');
  }
  const key=row.media_path||row.id,existing=sourceCache.current.get(key);
  if(existing&&existing.expires>Date.now())return existing.url;
  const url=await urlRef.current(row);
  if(!url)throw new Error('Could not load the source video.');
  sourceCache.current.set(key,{url,expires:Date.now()+7*60*1000});
  return url;
 },[activeId,localUrl]);
 const sourceIds=useMemo(()=>[...new Set(blocks.map(part=>part.sourceId))].join('|'),[blocks]);
 useEffect(()=>{
  let cancelled=false;
  const ids=sourceIds.split('|').filter(Boolean);
  setWaveformUrls({});
  for(const id of ids){
   resolveUrl(id).then(url=>{
    if(!cancelled)setWaveformUrls(previous=>({...previous,[id]:url}));
   }).catch(()=>{/* The selected player reports inaccessible source media. */});
  }
  return()=>{cancelled=true;};
 },[sourceIds,resolveUrl]);
 useEffect(()=>{
  const ticket=++request.current;
  const continuing=sequenceRef.current;
  playRef.current=null;mainVideo.current?.pause();nextVideo.current?.pause();
  setPlaying(false);setShowNext(false);setNextReady(false);setMediaError('');
  setMainUrl('');setNextUrl('');setLoadedBlockId(null);setLoadedNextId(null);
  if(!block)return undefined;
  setLoading(true);autoRef.current=continuing;
  resolveUrl(block.sourceId).then(url=>{
   if(request.current===ticket){setMainUrl(url);setLoadedBlockId(block.id);setLoading(false);}
  }).catch(error=>{
   if(request.current===ticket){setLoading(false);autoRef.current=false;sequenceRef.current=false;setMediaError(error.message);}
  });
  if(next)resolveUrl(next.sourceId).then(url=>{
   if(request.current===ticket){setNextUrl(url);setLoadedNextId(next.id);}
  }).catch(()=>{});
  return()=>{request.current++;};
 },[block?.id,block?.sourceId,next?.id,next?.sourceId,resolveUrl]);
 const seek=(i,time)=>{
  stop();setMessage('');setSelectedEdge(null);
  const target=blocks[clamp(i,0,blocks.length-1)];
  if(!target)return;
  const value=clamp(Number(time)||target.start,target.start,target.end);
  setPlayhead(value);
  if(i===active&&mainVideo.current&&mainUrl)mainVideo.current.currentTime=value;
  else{seekRef.current=value;setIndex(i);}
 };
 const save=nextPaper=>{
  paperRef.current=nextPaper;
  const nextProject={...projectRef.current,paperEdit:{...nextPaper,sourceColourOrder:colourSources}};
  projectRef.current=nextProject;onChange(nextProject);
 };
 const trim=(id,edge,value)=>{
  if(busy)return;
  try{
   const current=paperRef.current,part=current.blocks.find(b=>b.id===id);
   if(!part)return;
   const from=refineTrim(current,id,edge,value);
   if(from===current)return;
   const updated=refineTrim(prepareRefine(current),id,edge,value);
   stop();save(updated);setMessage('');
   setPlayhead(updated.blocks.find(b=>b.id===id)?.[edge]||0);
  }catch(error){setMessage(error.message);}
 };
 const protectEdges=()=>{
  if(busy)return;
  const current=paperRef.current,updated=protectLegacyWordEdges(current);
  if(updated===current){setMessage('No untouched older passages need speech handles.');return;}
  const count=updated.blocks.filter((part,i)=>part!==current.blocks[i]).length;
  stop();save(checkpointBeforeAiChange(current,updated,'Protect word edges'));
  setMessage('Protected word edges on '+count+' passage'+(count===1?'':'s')+'.');
 };
 const autoSpeechEdges=async()=>{
  if(busy||analysingEdges||!canAutoEdges||!mainUrl||!block)return;
  const initial=paperRef.current,id=block.id;
  setAnalysingEdges(true);setMessage('Analysing audio near this select’s cuts…');
  try{
   const updated=await assistWaveformPassages(initial,[id],()=>mainUrl);
   if(paperRef.current!==initial){
    setMessage('The Assembly changed during analysis. The existing edits were preserved.');
    return;
   }
   if(updated===initial){
    setMessage('No reliable quiet gap found. Your existing safe speech handles were kept.');
    return;
   }
   stop();
   save(checkpointBeforeAiChange(initial,updated,'Waveform speech edges'));
   setMessage('Audio-assisted cut points applied. Play across the cut and fine-tune if needed.');
  }catch(error){
   setMessage('Could not analyse this audio; your existing cut points were kept. '+(error?.message||''));
  }finally{setAnalysingEdges(false);}
 };
 const reset=()=>{
  if(busy||!block)return;
  try{
   const restored=restoreLockHandles(paperRef.current,block.id);
   if(restored!==paperRef.current){stop();save(restored);}
   setMessage('');
  }catch(error){setMessage(error.message);}
 };
 const startPlay=useCallback(async (kind,{restart=false}={})=>{
  if(!block||!mainUrl||!mainVideo.current)return;
  stop();setMediaError('');
  const from=kind==='assembly'?(restart?block.start:clamp(playhead,block.start,Math.max(block.start,block.end-.03))):
   kind==='cut'?Math.max(block.start,block.end-1.3):block.start;
  const end=block.end;
  sequenceRef.current=kind==='assembly';
  playRef.current={kind,end};
  try{
   mainVideo.current.currentTime=from;
   setPlayhead(from);
   await mainVideo.current.play();setPlaying(true);
  }catch(error){stop();setMediaError(error.message||'Unable to play this video.');}
 },[block,mainUrl,playhead,stop]);
 const onMainTime=()=>{
  const v=mainVideo.current,current=playRef.current;
  if(!v)return;
  setPlayhead(v.currentTime);
  if(!current||v.currentTime+.035<current.end)return;
  const action=refinePlaybackEndAction({
    kind:current.kind,active,count:blocks.length,loop:loopRef.current,
    incomingReady:Boolean(next&&nextReady&&nextUrl&&nextVideo.current)
  });
  if(action==='advance'||action==='restart-assembly'){
   if(action==='restart-assembly'&&active===0){
    void startPlay('assembly',{restart:true});return;
   }
   v.pause();playRef.current=null;sequenceRef.current=true;
   setIndex(action==='advance'?active+1:0);return;
  }
  if(action==='join'){
   v.pause();setShowNext(true);
   playRef.current={kind:'incoming',end:Math.min(next.end,next.start+1.4)};
   nextVideo.current.currentTime=next.start;
   void nextVideo.current.play().then(()=>setPlaying(true)).catch(error=>{
    stop();setMediaError(error.message||'Unable to play the next passage.');
   });
   return;
  }
  if(action==='restart-cut'){restartCutCycle();return;}
  stop();
 };
 const onNextTime=()=>{
  const v=nextVideo.current;
  if(v&&playRef.current?.kind==='incoming'&&v.currentTime+.035>=playRef.current.end){
   if(refinePlaybackEndAction({kind:'incoming',loop:loopRef.current})==='restart-cut'){
    restartCutCycle();
   }else stop();
  }
 };
 // Repeated cut playback deliberately restarts the existing loaded outgoing
 // video, avoiding React source resets and races between video elements.
 const restartCutCycle=()=>{
  if(!block||!mainVideo.current)return stop();
  const v=mainVideo.current;
  nextVideo.current?.pause();v.pause();setShowNext(false);
  const from=Math.max(block.start,block.end-1.3);
  playRef.current={kind:'cut',end:block.end};
  v.currentTime=from;setPlayhead(from);
  void v.play().then(()=>setPlaying(true)).catch(error=>{
   stop();setMediaError(error.message||'Unable to repeat this cut.');
  });
 };
 const onMainReady=e=>{
  if(!block||!shouldResumeRefineSequence({
    continuing:autoRef.current,loadedBlockId,activeBlockId:block.id,videoReady:true
  }))return;
  autoRef.current=false;sequenceRef.current=true;
  const v=e.currentTarget;
  v.currentTime=block.start;
  setPlayhead(block.start);
  playRef.current={kind:'assembly',end:block.end};
  void v.play().then(()=>setPlaying(true)).catch(error=>{
   stop();setMediaError(error.message||'Unable to continue playback.');
  });
 };
 // A same-source next passage can already have buffered media and therefore
 // no new canplay event. Resume it explicitly once this block's URL is ready.
 useEffect(()=>{
  const v=mainVideo.current;
  if(v&&mainUrl&&shouldResumeRefineSequence({
    continuing:autoRef.current,loadedBlockId,activeBlockId:block?.id,videoReady:v.readyState>=2
  })){
   onMainReady({currentTarget:v});
  }
 },[mainUrl,loadedBlockId,block?.id]);
 // The upcoming media can be cached too: don't depend solely on a second
 // canplay event when the same source is reused for multiple passages.
 useEffect(()=>{
  const v=nextVideo.current;
  if(v&&nextUrl&&loadedNextId===next?.id&&v.readyState>=2)setNextReady(true);
 },[nextUrl,loadedNextId,next?.id]);
 const nudge=(edge,frames)=>{
  if(!block)return;
  trim(block.id,edge,block[edge]+frames*step);
 };
 const beginDrag=(event,id,edge,kind)=>{
  if(event.button!==0||busy)return;
  event.preventDefault();event.stopPropagation();
  stop();setSelectedEdge(edge);setMessage('');
  const part=paperRef.current.blocks.find(b=>b.id===id);
  if(!part)return;
  const width=kind==='detail'?detailRef.current?.getBoundingClientRect().width:1;
  const secondsPerPixel=kind==='detail'?windowLength/Math.max(1,width):
   part.end-part.start>0?(part.end-part.start)/Math.max(1,event.currentTarget.closest('.rr-timeline-clip')?.getBoundingClientRect().width||px):1/px;
  dragRef.current={id,edge,startX:event.clientX,origin:part[edge],part,secondsPerPixel,lastSeek:0};
  event.currentTarget.setPointerCapture(event.pointerId);
 };
 const moveDrag=event=>{
  const d=dragRef.current;
  if(!d)return;
  const src=paperRef.current.sources?.[d.part.sourceId];
  const frame=sourceFrameStep(src)||.01;
  const max=sourceBounds(d.part,src).max;
  const minValue=d.edge==='start'?0:d.part.start+frame;
  const maxValue=d.edge==='start'?d.part.end-frame:max;
  const value=clamp(snapSourceTime(d.origin+(event.clientX-d.startX)*d.secondsPerPixel,src),minValue,maxValue);
  setDraft({id:d.id,edge:d.edge,value});
  if(d.id===block?.id&&mainVideo.current&&Date.now()-d.lastSeek>95){
   d.lastSeek=Date.now();mainVideo.current.currentTime=value;setPlayhead(value);
  }
 };
 const endDrag=(event,cancel=false)=>{
  const d=dragRef.current;
  if(!d)return;
  event.stopPropagation();
  if(event.currentTarget.hasPointerCapture(event.pointerId))event.currentTarget.releasePointerCapture(event.pointerId);
  dragRef.current=null;
  const src=paperRef.current.sources?.[d.part.sourceId];
  const frame=sourceFrameStep(src)||.01;
  const max=sourceBounds(d.part,src).max;
  const nextValue=clamp(snapSourceTime(d.origin+(event.clientX-d.startX)*d.secondsPerPixel,src),
   d.edge==='start'?0:d.part.start+frame,d.edge==='start'?d.part.end-frame:max);
  setDraft(null);
  if(!cancel)trim(d.id,d.edge,nextValue);
 };
 useEffect(()=>{
  const handler=event=>{
   if(busy||!block||typing(event.target)||event.altKey||event.ctrlKey||event.metaKey||
    document.querySelector('[role="dialog"]'))return;
   if(event.code==='Space')return; // App owns Space playback.
   if(selectedEdge&&(event.key==='ArrowLeft'||event.key==='ArrowRight')){
    event.preventDefault();nudge(selectedEdge,(event.key==='ArrowLeft'?-1:1)*(event.shiftKey?5:1));
   }
  };
  window.addEventListener('keydown',handler);
  return()=>window.removeEventListener('keydown',handler);
 });
 const reorderCard=(from,to,animate=true)=>{
  const current=paperRef.current;
  if(busy||from===to||from<0||to<0||from>=current.blocks.length||to>=current.blocks.length)return;
  for(const animation of reorderAnimations.current)animation.cancel();
  reorderAnimations.current=[];
  reorderPositions.current=animate?capturePassageRects(assemblyRef.current):null;
  stop();setIndex(to);save(movePassage(current,from,to));
 };
 const cardSlot=y=>{
  const list=assemblyRef.current;if(!list)return 0;
  const preview=cardDragRef.current?.preview;
  if(preview){
   const {slot}=preview.update(cardDragRef.current.x,y);
   cardDragRef.current.slot=slot;return slot;
  }
  const rects=[...list.querySelectorAll('[data-passage-id]')].map(node=>node.getBoundingClientRect());
  const slot=passageInsertSlot(rects,y);
  if(cardDragRef.current)cardDragRef.current.slot=slot;
  setDropSlot(previous=>previous===slot?previous:slot);
  return slot;
 };
 const stopCardScroll=()=>{
  if(cardScrollRaf.current!==null)cancelAnimationFrame(cardScrollRaf.current);
  cardScrollRaf.current=null;
 };
 const cardAutoScroll=()=>{
  const state=cardDragRef.current;
  if(!state?.started){cardScrollRaf.current=null;return;}
  const list=assemblyRef.current;
  if(list){
   const bounds=list.getBoundingClientRect(),edge=42;
   let delta=0;
   if(state.y<bounds.top+edge)delta=-Math.min(17,(bounds.top+edge-state.y)*.45);
   else if(state.y>bounds.bottom-edge)delta=Math.min(17,(state.y-(bounds.bottom-edge))*.45);
   if(delta)list.scrollTop+=delta;
   cardSlot(state.y);
  }
  cardScrollRaf.current=requestAnimationFrame(cardAutoScroll);
 };
 const beginCardDrag=(event,index,id)=>{
  if(busy||event.button!==0||event.isPrimary===false||headingDrag.current)return;
  event.preventDefault();event.stopPropagation();
  cardDragRef.current={pointerId:event.pointerId,index,id,startX:event.clientX,startY:event.clientY,
   x:event.clientX,y:event.clientY,slot:index,started:false,preview:null};
  event.currentTarget.setPointerCapture(event.pointerId);
  setIndex(index);
 };
 const moveCardDrag=event=>{
  const state=cardDragRef.current;
  if(!state||event.pointerId!==state.pointerId)return;
  if(!state.started){
   if(Math.hypot(event.clientX-state.startX,event.clientY-state.startY)<6)return;
   state.started=true;
   state.preview=createFloatingPassageDrag(assemblyRef.current,
    event.currentTarget.closest('[data-passage-id]'),state.index,event.clientX,event.clientY);
   setDraggingCard(state.id);
   if(cardScrollRaf.current===null)cardScrollRaf.current=requestAnimationFrame(cardAutoScroll);
  }
  state.x=event.clientX;state.y=event.clientY;
  event.preventDefault();event.stopPropagation();cardSlot(event.clientY);
 };
 const finishCardDrag=(event,cancel=false)=>{
  const state=cardDragRef.current;
  if(!state||event.pointerId!==state.pointerId)return;
  const moved=state.started;
  const slot=moved&&!cancel?cardSlot(event.clientY):null;
  const destination=slot===null?null:passageReorderTarget(state.index,slot,paperRef.current.blocks.length);
  state.preview?.finish();
  cardDragRef.current=null;stopCardScroll();setDraggingCard(null);setDropSlot(null);
  if(event.currentTarget.hasPointerCapture?.(event.pointerId))event.currentTarget.releasePointerCapture(event.pointerId);
  if(moved){
   event.preventDefault();event.stopPropagation();
   // Suppress the post-pointerup click that previously reselected a stale card.
   suppressClickUntil.current=Date.now()+450;
  }
  if(destination!==null&&destination!==state.index)reorderCard(state.index,destination,false);
 };
 useLayoutEffect(()=>{
  const previous=reorderPositions.current;
  if(!previous)return;
  reorderPositions.current=null;
  const reducedMotion=window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  reorderAnimations.current=animatePassageReorder(assemblyRef.current,previous,{reducedMotion});
 },[paper.blocks]);
 useEffect(()=>()=>{for(const animation of reorderAnimations.current)animation.cancel();},[]);
 useEffect(()=>()=>{stopCardScroll();cardDragRef.current?.preview?.destroy();cardDragRef.current=null;},[]);
 useEffect(()=>{
  const list=assemblyRef.current,selected=list?.querySelector('.paper-block.selected');
  if(!list||!selected||draggingCard||headingDrag.current||reorderPositions.current)return;
  const a=selected.getBoundingClientRect(),b=list.getBoundingClientRect();
  if(a.bottom>b.bottom-8)list.scrollTop+=a.bottom-b.bottom+12;
  else if(a.top<b.top+8)list.scrollTop-=b.top-a.top+12;
 },[active,draggingCard]);
 const endHeadingDrag=()=>{
  headingDrag.current=null;setDraggingCard(null);setHeadingDrop(null);setDropSlot(null);
 };
 const placeHeadingInGap=slot=>{
  const id=headingDrag.current;
  if(!id)return;
  save(placeStorySection(paperRef.current,id,slot));endHeadingDrag();
 };
 const renderSections=at=>(paper.sectionHeadings||[]).filter(section=>section.beforeIndex===at).map(section=>
  <StorySectionHeading key={section.id} section={section} maxIndex={blocks.length}
   dragging={headingDrag.current===section.id}
   dropSide={headingDrop?.id===section.id?headingDrop.side:null}
   onDragStart={(event,id)=>{
    if(busy){event.preventDefault();return;}
    headingDrag.current=id;cardDragRef.current=null;stopCardScroll();
    setDraggingCard('heading:'+id);setHeadingDrop(null);setDropSlot(null);
    event.dataTransfer.effectAllowed='move';
    event.dataTransfer.setData('text/plain','takefold-heading:'+id);
   }}
   onDragOver={(event,id)=>{
    if(!headingDrag.current||headingDrag.current===id)return;
    event.preventDefault();event.stopPropagation();
    event.dataTransfer.dropEffect='move';
    const rect=event.currentTarget.getBoundingClientRect();
    setHeadingDrop({id,side:event.clientY>rect.top+rect.height/2?'after':'before'});
    setDropSlot(null);
   }}
   onDrop={(event,id)=>{
    if(!headingDrag.current)return;
    event.preventDefault();event.stopPropagation();
    const rect=event.currentTarget.getBoundingClientRect();
    save(placeStorySectionBeside(paperRef.current,headingDrag.current,id,
     event.clientY>rect.top+rect.height/2));
    endHeadingDrag();
   }}
   onDragEnd={endHeadingDrag}
   onRename={(id,text)=>{if(!busy)save(renameStorySection(paperRef.current,id,text));}}
   onMove={(id,direction)=>{if(!busy)save(moveStorySection(paperRef.current,id,direction));}}
   onRemove={id=>{if(!busy)save(removeStorySection(paperRef.current,id));}}/>);
 const removeCard=id=>{
  if(busy)return;
  const current=paperRef.current,at=current.blocks.findIndex(part=>part.id===id);
  if(at<0)return;
  stop();setIndex(Math.max(0,Math.min(active,at===active?current.blocks.length-2:active-(at<active?1:0))));
  save(removeStoryPassage(current,id));
 };
 useEffect(()=>{
  const scroller=scrollRef.current,selected=scroller?.querySelector('.rr-timeline-clip.is-active');
  if(!scroller||!selected)return;
  const viewport=scroller.getBoundingClientRect(),clip=selected.getBoundingClientRect();
  if(clip.right>viewport.right-24)scroller.scrollLeft+=clip.right-viewport.right+38;
  else if(clip.left<viewport.left+24)scroller.scrollLeft-=viewport.left-clip.left+38;
 },[active,zoom]);
 useEffect(()=>{
  if(!playbackRef)return undefined;
  playbackRef.current=()=>{if(playing)stop();else void startPlay('assembly');};
  return()=>{playbackRef.current=null;};
 },[playbackRef,playing,stop,startPlay]);
 useEffect(()=>()=>{request.current++;stop();},[stop]);
 if(!block)return <section className="rr-empty" aria-label="Empty Refine workspace">
  <div className="rr-empty-card">
   <span className="rr-empty-icon" aria-hidden="true"><FiScissors/></span>
   <h2>Nothing to refine yet</h2>
   <p>Add some interview passages to your Assembly in Paper Edit. Then come back to fine-tune the cuts.</p>
   <button type="button" className="rr-empty-back" onClick={onBack} style={{backgroundColor:'#16834a',borderColor:'#16834a',color:'#ffffff',opacity:1}}><FiArrowLeft aria-hidden="true"/><span>Back to Paper Edit</span></button>
   <small>Refine is optional — you can export directly from Paper Edit.</small>
  </div>
 </section>;
 return <section className="rr" aria-label="Refine editor">
  <header className="rr-header">
   <div className="rr-heading"><h2>Refine cuts</h2><span>{activeCutName(paper)} · Fine-tune Assembly passage boundaries</span></div>
   <div className="rr-header-actions">
    {hasLegacyWordEdges&&<button type="button" disabled={busy} onClick={protectEdges}
      title="Add small speech handles to untouched older cuts; manual trims remain unchanged.">
      <FiScissors/> Protect word edges
     </button>}
    <button type="button" onClick={onBack}><FiArrowLeft/> Paper Edit</button>
    <button type="button" className="rr-export" onClick={onExport}><FiDownload/> Export <FiArrowRight/></button>
   </div>
  </header>
  <div className="rr-stage">
   <section className="rr-assembly paper-story paper-workspace is-compact-story" aria-label="Assembly selects">
    <div className="rr-assembly-heading paper-story-heading">
     <div className="rr-assembly-title"><h3>Assembly</h3><span className="count">{blocks.length} selects · {clock(total)}</span></div>
     <small>Drag to reorder · Refine the same edit</small>
    </div>
    <div className={'rr-assembly-list paper-blocks'+(draggingCard?' is-reordering':'')} ref={assemblyRef}
     onDragOver={event=>{
      if(!headingDrag.current||event.target!==event.currentTarget)return;
      event.preventDefault();event.dataTransfer.dropEffect='move';
      setDropSlot(blocks.length);setHeadingDrop(null);
     }}
     onDrop={event=>{
      if(!headingDrag.current||event.target!==event.currentTarget)return;
      event.preventDefault();placeHeadingInGap(blocks.length);
     }}>
     {blocks.map((part,i)=>{
      const partSource=paper.sources?.[part.sourceId];
      return <React.Fragment key={part.id}>
       {renderSections(i)}
       <article data-passage-id={part.id} tabIndex={0}
        className={'paper-block'+(i===active?' selected':'')+(draggingCard===part.id?' dragging':'')+(dropSlot===i?' drop-before':'')}
        style={interviewColour(part.sourceId,colourSources)}
        aria-label={'Passage '+(i+1)+': '+selectText(part)}
        onClick={event=>{
         if(Date.now()<suppressClickUntil.current||cardDragRef.current)return;
         if(!event.target.closest('button,input,select,textarea'))seek(i,part.start);
        }}
        onKeyDown={event=>{
         if(event.target!==event.currentTarget)return;
         if(event.altKey&&(event.key==='ArrowUp'||event.key==='ArrowDown')){
          event.preventDefault();reorderCard(i,i+(event.key==='ArrowUp'?-1:1));
         }else if(event.key==='Enter'){event.preventDefault();seek(i,part.start);}
        }}
        onDragOver={event=>{
         if(!headingDrag.current)return;
         event.preventDefault();event.dataTransfer.dropEffect='move';
         const rect=event.currentTarget.getBoundingClientRect();
         setDropSlot(i+(event.clientY>rect.top+rect.height/2?1:0));setHeadingDrop(null);
        }}
        onDrop={event=>{
         if(!headingDrag.current)return;
         event.preventDefault();
         const rect=event.currentTarget.getBoundingClientRect();
         placeHeadingInGap(i+(event.clientY>rect.top+rect.height/2?1:0));
        }}>
        <header>
         <button type="button" className="passage-grip" disabled={busy}
          aria-label={'Drag passage '+(i+1)+' to reorder; Alt plus up or down arrow to reorder by keyboard'}
          title="Drag to reorder · Alt + ↑/↓ to move"
          onPointerDown={event=>beginCardDrag(event,i,part.id)}
          onPointerMove={moveCardDrag}
          onPointerUp={finishCardDrag}
          onPointerCancel={event=>finishCardDrag(event,true)}
          onLostPointerCapture={event=>finishCardDrag(event,true)}
          onClick={event=>event.stopPropagation()}
          onKeyDown={event=>{
           if(event.altKey&&(event.key==='ArrowUp'||event.key==='ArrowDown')){
            event.preventDefault();event.stopPropagation();
            reorderCard(i,i+(event.key==='ArrowUp'?-1:1));
           }
          }}><svg width="20" height="24" viewBox="0 0 20 24" aria-hidden="true" focusable="false">
           {[5,12,19].flatMap(y=>[6,14].map(x=><circle key={x+'-'+y} cx={x} cy={y} r="1.7" fill="currentColor"/>))}
          </svg></button>
         <strong><span className="paper-block-index">{String(i+1).padStart(2,'0')}</span>
          <span className="paper-block-source" title={sourceName(part.sourceId)}>{sourceName(part.sourceId)}</span>
          
         </strong>
         <span className="paper-block-duration">{clock(Math.max(0,part.end-part.start))}</span>
         <button className="passage-delete" type="button" disabled={busy}
          aria-label={'Remove passage '+(i+1)+' from edit'} title="Remove passage"
          onClick={event=>{event.stopPropagation();removeCard(part.id);}}><FiTrash2 aria-hidden="true"/></button>
        </header>
        <p className="paper-comment-text">{selectText(part)}</p>
        {part.editorialReason&&<details className="paper-ai-rationale" onClick={event=>event.stopPropagation()}>
         <summary>{({beginning:'Beginning',middle:'Middle',end:'End'})[part.editorialSection]||'AI edit'} · Why this passage?</summary>
         <p>{part.editorialReason}</p>{part.editorialContext&&<p><strong>Check:</strong> {part.editorialContext}</p>}
        </details>}
        {part.editorialFlowReason&&<details className="paper-ai-rationale" onClick={event=>event.stopPropagation()}>
         <summary>AI flow · Why this position?</summary><p>{part.editorialFlowReason}</p>
        </details>}
       </article>
      </React.Fragment>;
     })}
     {renderSections(blocks.length)}
     {dropSlot===blocks.length&&draggingCard&&<div className="paper-drop-end" aria-hidden="true"/>}
    </div>
   </section>
   <div className="rr-player">
    <div className="rr-monitor">
     <video ref={mainVideo} className={showNext?'is-hidden':''} src={mainUrl||undefined}
      playsInline preload="auto" onTimeUpdate={onMainTime} onCanPlay={onMainReady}
      onLoadedMetadata={e=>{if(block){e.currentTarget.currentTime=seekRef.current===null?block.start:seekRef.current;
       seekRef.current=null;setPlayhead(e.currentTarget.currentTime);}}}
      onEnded={()=>{if(playRef.current?.kind!=='incoming')onMainTime();}}
      onError={()=>setMediaError('Browser cannot play this source video.')}/>
     <video ref={nextVideo} className={!showNext?'is-hidden':''} src={nextUrl||undefined}
      playsInline preload="auto" onTimeUpdate={onNextTime}
      onLoadedMetadata={e=>{if(next)e.currentTarget.currentTime=next.start;}}
      onCanPlay={()=>{if(loadedNextId===next?.id)setNextReady(true);}}
      onEnded={onNextTime} onError={()=>setNextReady(false)}/>
     {!mainUrl&&<div className="rr-monitor-placeholder">{loading?'Loading interview…':mediaError||'Reconnect this video in Paper Edit'}</div>}
     {showNext&&<span className="rr-next-label">NEXT INTERVIEW</span>}
    </div>
    <div className="rr-transport">
     <button type="button" disabled={!mainUrl} onClick={()=>seek(Math.max(0,active-1),blocks[Math.max(0,active-1)]?.start)} aria-label="Previous select"><FiSkipBack/></button>
     <button type="button" className="rr-transport-play" disabled={!mainUrl} onClick={()=>playing?stop():void startPlay('assembly')}>
      {playing?<FiPause/>:<FiPlay/>} {playing?'Pause':'Play assembly'}
     </button>
     <button type="button" disabled={!mainUrl||!next} onClick={()=>seek(active+1,next?.start)} aria-label="Next select"><FiSkipForward/></button>
     <time>{clock(playhead)} <span>/ {clock(sourceMax)}</span></time>
     <button type="button" className={'rr-loop-toggle'+(loopEnabled?' is-active':'')}
      aria-pressed={loopEnabled} aria-label="Loop playback"
      title={loopEnabled?'Loop is on · repeats the current cut or Assembly':'Loop is off · click to repeat playback'}
      onClick={toggleLoop}><FiRepeat aria-hidden="true"/><span>Loop</span></button>
     <button type="button" className="rr-play-cut" disabled={!mainUrl} onClick={()=>playing?stop():void startPlay('cut')}>
      {playing?'Stop playback':'Play this cut'} <FiArrowRight/>
     </button>
    </div>
   </div>
  </div>
  {mediaError&&mainUrl&&<p className="rr-error" role="alert">{mediaError}</p>}
  <section className="rr-timeline-panel" aria-label="Assembly timeline">
   <div className="rr-panel-head">
    <div><strong>Assembly timeline</strong><span>{blocks.length} selects · {clock(total)}</span></div>
    <div className="rr-zoom"><button aria-label="Zoom out" disabled={zoom===0} onClick={()=>setZoom(z=>Math.max(0,z-1))}><FiMinus/></button>
     <span>{px} px/s</span><button aria-label="Zoom in" disabled={zoom===pixels.length-1} onClick={()=>setZoom(z=>Math.min(pixels.length-1,z+1))}><FiPlus/></button></div>
   </div>
   <div className="rr-timeline-scroll" ref={scrollRef}>
    <div className="rr-ruler"><span>00:00</span><span>ASSEMBLY · {clock(total)}</span></div>
    <div className="rr-timeline-track">
     {timeline.map((part,i)=>{
      const partSource=paper.sources?.[part.sourceId];
      const isActive=i===active;
      const partHead=isActive?clamp((playhead-part.start)/Math.max(part.duration,.01)*100,0,100):0;
      return <div key={part.id} className={'rr-timeline-clip'+(isActive?' is-active':'')}
       style={{width:Math.max(82,Math.round(part.duration*px))+'px'}}
       title={'Select '+(i+1)+' · '+title(partSource,part)}
       onClick={event=>{if(dragRef.current)return;const rect=event.currentTarget.getBoundingClientRect();
        const fraction=clamp((event.clientX-rect.left)/Math.max(1,rect.width),0,1);
        seek(i,part.start+fraction*part.duration);}}>
       <div className="rr-clip-top"><span>{String(i+1).padStart(2,'0')}</span><span>{clock(part.duration)}</span></div>
       <div className="rr-clip-label">{title(partSource,part)}</div>
       <div className="rr-clip-speech" aria-hidden="true">
        <RefineWaveform url={waveformUrls[part.sourceId]||(part.sourceId===block.sourceId?mainUrl:'')} start={part.start} end={part.end}/>
       </div>
       {isActive&&<div className="rr-clip-playhead" style={{left:partHead+'%'}}/>}
       {isActive&&['start','end'].map(edge=><button key={edge} type="button"
        className={'rr-clip-handle rr-'+edge} style={draft&&draft.id===part.id&&draft.edge===edge?{transform:'translateX('+((draft.value-part[edge])*px)+'px)'}:undefined} aria-label={'Drag '+edge+' of select '+(i+1)}
        title={'Drag to trim '+(edge==='start'?'IN':'OUT')}
        onClick={e=>e.stopPropagation()} onPointerDown={e=>beginDrag(e,part.id,edge,'timeline')}
        onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={e=>endDrag(e,true)}/>)}
      </div>;
     })}
    </div>
   </div>
   <div className="rr-timeline-foot"><span><i/> Selected clip</span><span>Click to seek · Drag green edges to trim · Sequence ripples automatically</span></div>
  </section>
  <section className="rr-detail" aria-label="Fine tune source handles">
   <div className="rr-detail-head">
    <div><strong>Trim select {active+1}</strong><span>Grey is unused source · Green stays in the edit</span></div>
    <div className="rr-detail-actions">
     {canAutoEdges&&<button type="button" onClick={()=>void autoSpeechEdges()}
      disabled={busy||analysingEdges||!mainUrl} title="Use real source audio to find quieter, safer cut positions. Manual trims are preserved.">
      <FiScissors/> {analysingEdges?'Checking audio…':'Auto speech edges'}
     </button>}
     <button type="button" disabled={detailZoom<=.5} onClick={()=>setDetailZoom(z=>Math.max(.5,z/2))} title="Zoom in around the handles"><FiPlus/> Zoom</button>
     <button type="button" disabled={detailZoom>=12} onClick={()=>setDetailZoom(z=>Math.min(12,z*2))} title="Show more surrounding footage"><FiMinus/></button>
     {changed&&<button type="button" onClick={reset} disabled={busy}><FiRotateCcw/> Reset</button>}
    </div>
   </div>
   <div className="rr-detail-ruler"><span>{clock(windowStart)}</span><span>SOURCE FOOTAGE</span><span>{clock(windowEnd)}</span></div>
   <div className="rr-detail-rail" ref={detailRef}>
    <div className="rr-kept" style={{left:percent(inPoint)+'%',width:Math.max(0,percent(outPoint)-percent(inPoint))+'%'}}/>
    <RefineWaveform detail url={mainUrl} start={windowStart} end={windowEnd}/>
    {['start','end'].map(edge=><button key={edge} type="button" className={'rr-detail-handle rr-'+edge+(selectedEdge===edge?' chosen':'')}
     style={{left:percent(edge==='start'?inPoint:outPoint)+'%'}} aria-label={'Drag '+(edge==='start'?'IN':'OUT')+' point'}
     title={'Drag '+(edge==='start'?'IN':'OUT')+' · arrow keys adjust by a frame'}
     onClick={()=>setSelectedEdge(edge)} onPointerDown={e=>beginDrag(e,block.id,edge,'detail')}
     onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={e=>endDrag(e,true)}>
     <span>{edge==='start'?'IN':'OUT'}</span>
    </button>)}
    {playhead>=windowStart&&playhead<=windowEnd&&<span className="rr-detail-playhead" style={{left:percent(playhead)+'%'}}/>}
   </div>
   <div className="rr-detail-values">
    <button type="button" className={selectedEdge==='start'?'chosen':''} onClick={()=>setSelectedEdge('start')}>
     <span>IN</span><strong>{formatSourceFrames(inPoint,source)}</strong></button>
    <span className="rr-kept-duration">{clock(Math.max(0,outPoint-inPoint))} kept</span>
    <button type="button" className={selectedEdge==='end'?'chosen':''} onClick={()=>setSelectedEdge('end')}>
     <span>OUT</span><strong>{formatSourceFrames(outPoint,source)}</strong></button>
   </div>
   {warnings.length>0&&<p className="rr-flag"><FiAlertTriangle/> {warnings.map(w=>w.label).join(' · ')}</p>}
   {message&&<p className="rr-note" role="status">{message}</p>}
   <details className="rr-shortcuts"><summary><FiInfo/> Shortcuts</summary>
    <p>Space: play/pause · Loop repeats the current cut or Assembly · Click a handle, then ← →: one frame · Shift + ← →: five frames · Ctrl/⌘ Z: undo.</p>
   </details>
  </section>
 </section>;
}
